import React, { Fragment, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch } from "hooks/redux";
import Page from "components/page/Page";
import Input from "components/form/input/Input";
import Card from "components/card/Card";
import {
  DeleteFoodOrderingCategories,
  DeleteFoodOrderingCategoryProduct,
  GetFoodOrderingCategories,
  GetFoodOrderingCategoryProducts,
  PostFoodOrderingCategoryProduct,
  PutFoodOrderingCategories,
} from "api/rpc/2024-04/facilityAdmin/facility/foodOrdering";
import Popup from "components/popup/Popup";
import { unsavedChangesExist } from "helpers/Helpers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonNew as Button } from "components/buttonNew/index";
import { IProduct } from "redux/reducers/models/product";
import Checkbox from "components/form/checkbox/Checkbox";
import { GetProduct as FacilityGetProduct } from "api/rpc/2024-04/facilityAdmin/product/product";
import Sheet from "components/sheet/Sheet";
import classNames from "classnames";
import Search from "components/search/Search";
import { cloneDeep } from "lodash";
import Spin from "components/spin/spin";
import DataTable from "components/dataTable/DataTable";
import "./foodOrdering.scss";

interface IFoodOrderingCategoryState {
  addedProducts: IProduct[];
  searchQuery: string;
  products: IProduct[];
  addProductVisibility: boolean;
  deleteProductVisibility: boolean;
  selectedVariants: Array<number>;
  selectedProducts: Array<IProduct>;
  deleteModalVisible: boolean;
}

interface IFilterState {
  title: string;
}

export default function EditFoodOrderingCategory() {
  const history = useHistory();
  const { t } = useTranslation();
  const { id }: any = useParams();

  const [foodOrderingState, setFoodOrderingState] = useState<IFoodOrderingCategoryState>({
    addedProducts: [],
    searchQuery: "",
    products: [],
    addProductVisibility: false,
    deleteProductVisibility: false,
    selectedProducts: [],
    selectedVariants: [],
    deleteModalVisible: false,
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    title: "",
  });

  const [filterStateBeforeChanges, setFilterStateBeforeChanges] = useState<IFilterState>(filterState);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadFoodOrderingCategories();

    return () => {
      setFoodOrderingState({
        products: null,
        searchQuery: "",
        addProductVisibility: false,
        deleteProductVisibility: false,
        addedProducts: [],
        selectedVariants: [],
        selectedProducts: [],
        deleteModalVisible: false,
      });

      source.cancel();
    };
  }, []);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadProducts(foodOrderingState.searchQuery, source.token);
    return () => source.cancel();
  }, [foodOrderingState.searchQuery]);

  const dispatch = useAppDispatch();

  async function loadFoodOrderingCategories() {
    const params = {
      id: Number(id),
    };
    const categoryRes = await GetFoodOrderingCategories(params, true);

    console.log(categoryRes);

    if (categoryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading settings")));
      return;
    }

    const categoryProductsParams = {
      category_id: Number(id),
    };

    const productsInCategoryRes = await GetFoodOrderingCategoryProducts(categoryProductsParams, true);

    console.log(productsInCategoryRes);

    if (productsInCategoryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading products")));
      return;
    }

    setFilterState((prevState) => ({ ...prevState, title: categoryRes.data[0]?.title }));
    setFilterStateBeforeChanges((prevState) => ({ ...prevState, title: categoryRes.data[0]?.title }));
    setFoodOrderingState((prevState) => ({ ...prevState, addedProducts: productsInCategoryRes.data }));
  }

  function handleInputChange(e: any) {
    const id = e.target.id;
    const value = e.target.value;

    setFilterState((prev) => ({ ...prev, [id]: value }));
  }

  async function handleSaveCategory() {
    const params = {
      id: Number(id),
      title: filterState.title,
    };

    const saveCategoryRes = await PutFoodOrderingCategories(params, true);

    if (saveCategoryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error saving category")));
      return;
    }

    dispatch(showSuccess(t("Successfully saved category")));
    setFilterStateBeforeChanges(filterState);
  }

  async function handleDeleteCategory() {
    const params = {
      id: Number(id),
    };

    const deleteCategoryRes = await DeleteFoodOrderingCategories(params, true);

    if (deleteCategoryRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting category")));
      return;
    }

    dispatch(showSuccess(t("Successfully deleted category")));
    history.push("/admin/food-ordering/overview");
  }

  async function loadProducts(search: string, token: CancelToken) {
    if (foodOrderingState.products !== undefined) {
      setFoodOrderingState((prevState) => ({ ...prevState, products: undefined }));
    }

    const res = await FacilityGetProduct({ search: search ?? undefined }, false, token);
    if (res.status !== StatusCode.OK) {
      if (token?.reason) {
        return;
      }
      dispatch(showError(t("Error loading products")));
    }

    setFoodOrderingState((prevState) => ({ ...prevState, products: res.status !== StatusCode.OK ? [] : res.data }));
  }

  async function deleteProducts() {
    if (foodOrderingState.selectedVariants.length <= 0) {
      dispatch(showError(t("A product to delete must be selected")));
      return;
    }

    const res = await DeleteFoodOrderingCategoryProduct(
      {
        category_id: Number(id),
        product_ids: foodOrderingState.selectedVariants,
      },
      true,
    );

    console.log(res);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error deleting product from group")));
      return;
    }
    setFoodOrderingState((prevState) => ({ ...prevState, selectedVariants: [] }));
    void loadFoodOrderingCategories();
    void handleDeleteProductsVisibility();
  }

  function handleAddProductsVisibility() {
    setFoodOrderingState((prevState) => ({ ...prevState, addProductVisibility: !prevState.addProductVisibility }));
  }

  function handleDeleteProductsVisibility() {
    if (foodOrderingState.selectedVariants.length <= 0) {
      dispatch(showError(t("Please select a variant to remove.")));
      return;
    }
    setFoodOrderingState((prevState) => ({
      ...prevState,
      deleteProductVisibility: !prevState.deleteProductVisibility,
    }));
  }

  function handleToggleAllSelectedVariants() {
    if (foodOrderingState?.selectedVariants?.length !== foodOrderingState.addedProducts?.length) {
      setFoodOrderingState((prev) => ({
        ...prev,
        selectedVariants: foodOrderingState.addedProducts.map((variant: { id: number }) => variant.id),
      }));
    } else {
      setFoodOrderingState((prev) => ({ ...prev, selectedVariants: [] }));
    }
  }

  function handleSelectVariant(variant_id: number) {
    const foundId = foodOrderingState.selectedVariants.filter((val) => val === variant_id)[0];

    if (foundId) {
      setFoodOrderingState((prev) => ({
        ...prev,
        selectedVariants: prev.selectedVariants.filter((val) => val !== variant_id),
      }));
    } else {
      setFoodOrderingState((prev) => ({ ...prev, selectedVariants: [...prev.selectedVariants, variant_id] }));
    }
  }

  async function postFoodOrderingCategoryProduct() {
    const productIds: Array<number> = [];
    foodOrderingState?.selectedProducts?.forEach((product) => {
      productIds.push(product?.id);
    });

    console.log(productIds);

    if (productIds?.length <= 0) {
      dispatch(showError(t("A product must be selected")));
      return;
    }

    const postRes = await PostFoodOrderingCategoryProduct(
      {
        category_id: id,
        product_ids: productIds,
      },
      true,
    );

    console.log(postRes);

    if (postRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error adding product to group")));
    }

    setFoodOrderingState((prevState) => ({ ...prevState, selectedProducts: [], searchQuery: "" }));
    void loadFoodOrderingCategories();
    void handleAddProductsVisibility();
  }

  function handleSearch(searchValue: string) {
    setFoodOrderingState((prevState) => ({ ...prevState, searchQuery: searchValue }));
  }

  function handleSelectProduct(productIndex: number) {
    const updatedSelectedProducts = [...foodOrderingState.selectedProducts];
    const currentProduct = cloneDeep(foodOrderingState.products[productIndex]);
    const foundProductIndex = updatedSelectedProducts?.findIndex(
      (selectedProduct) => selectedProduct?.id === currentProduct?.id,
    );

    // Single variant selected
    if (foundProductIndex === -1) {
      // Add product and single variant
      const updatedCurrentProduct = { ...currentProduct };
      updatedSelectedProducts.push(updatedCurrentProduct);
    } else {
      updatedSelectedProducts?.splice(foundProductIndex, 1);
    }

    setFoodOrderingState((prevState) => ({ ...prevState, selectedProducts: updatedSelectedProducts }));
  }

  const secondaryAction = {
    content: t("Delete"),
    action: () => setFoodOrderingState((prevState) => ({ ...prevState, deleteModalVisible: true })),
  };

  return (
    <>
      <Page
        title={
          filterState.title
            ? filterState.title
            : t("Edit Category")
        }
        primaryAction={{
          content: t("Save"),
          action: handleSaveCategory,
          // action: () => loadFoodOrderingCategories(),
          disabled: filterState.title === "",
        }}
        secondaryActions={[secondaryAction]}
        breadcrumbs={[
          {
            prefix: true,
            label: t("Back to overview"),
            url: "/admin/food-ordering/overview",
          },
        ]}
        notificationBarProps={{
          isVisible: unsavedChangesExist(filterState, filterStateBeforeChanges),
          onAction: () => handleSaveCategory(),
          onCancel: () => setFilterState(filterStateBeforeChanges),
        }}
      >
        <Card>
          <Card.Section>
            <div>
              <Input
                placeholder={t("Category Title...")}
                id="title"
                value={filterState.title}
                onChange={handleInputChange}
                label={t("Category Title")}
              />
            </div>
          </Card.Section>

          <Card.Section>
            <div className="product-group-header">
              <div className="title-header">
                {t("Products")}
              </div>

              {/* {!productState.groupHasSubGroups && ( */}
              <div className="flex items-center">
                <Button className="ml-2" type="secondary" size="xsmall" onClick={handleAddProductsVisibility}>
                  <FontAwesomeIcon icon={["fas", "plus"]} className="mr-2" />{" "}
                  <span>{t("Add")}</span>
                </Button>

                <Button className="ml-2" type="secondary" size="xsmall" onClick={handleDeleteProductsVisibility}>
                  <FontAwesomeIcon icon={["far", "trash-can"]} className="mr-2" />{" "}
                  <span>{t("Remove")}</span>
                </Button>

                {/* <Button className="ml-2" type="secondary" size="xsmall" onClick={handleCopyProductsVisibility}>
                        <FontAwesomeIcon icon={["fas", "copy"]} className="mr-2" /> <span>Copy</span>
                      </Button> */}
              </div>
              {/* )} */}
            </div>

            <DataTable
              columns={[
                {
                  label: "",
                  width: "7%",
                  content: (
                    <Checkbox
                      onChange={() => handleToggleAllSelectedVariants()}
                      size="small"
                      checked={foodOrderingState?.selectedVariants?.length === foodOrderingState.addedProducts?.length}
                    />
                  ),
                },
                { label: t("Title"), width: "31%" },
                { label: t("Subtitle"), width: "31%" },
                { label: t("Type"), width: "31%" },
              ]}
              loading={foodOrderingState.addedProducts === undefined}
            >
              <Fragment>
                {foodOrderingState.addedProducts?.length > 0 ? (
                  foodOrderingState.addedProducts?.map((product: any, index: number) => {
                    return (
                      <tr key={index} className="clickable" onClick={() => handleSelectVariant(product.id)}>
                        <td>
                          <Checkbox
                            onChange={(e) => e.stopPropagation()}
                            size="small"
                            checked={foodOrderingState?.selectedVariants?.includes(product.id)}
                          />
                        </td>
                        <td>{product?.title}</td>
                        <td>{product?.subtitle}</td>
                        <td>{product?.type}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={4}>
                      <div className="food-ordering-category-no-products-container">
                        <div className="food-ordering-category-no-products-icon">
                          <FontAwesomeIcon icon={["fas", "magnifying-glass"]} />
                        </div>
                        <div>
                          <p className="food-ordering-category-no-products-title">
                            {t("No Products Added")}
                          </p>
                        </div>

                        <div>
                          <p className="food-ordering-category-no-products-subtitle">
                            {t("There are no products added to your category yet.")}
                          </p>
                        </div>

                        <div>
                          <p className="food-ordering-category-no-products-subtitle">
                            {t("Please add a new product")}
                          </p>
                        </div>

                        <div className="mt-3 mb-4">
                          <Button
                            // className="ml-2"
                            type="primary"
                            // size="xsmall"
                            onClick={handleAddProductsVisibility}
                          >
                            <FontAwesomeIcon icon={["far", "plus"]} className="mr-2" />{" "}
                            <span>{t("Add Product")}</span>
                          </Button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            </DataTable>
          </Card.Section>
        </Card>
      </Page>

      {/* Add Products Sheet */}
      <div className="pricing-sheets-products-modal">
        <Sheet
          closable
          onCancel={handleAddProductsVisibility}
          size="medium"
          open={foodOrderingState.addProductVisibility}
          cancelText={t("Close")}
          okText={t("Add")}
          title={t("Add Products")}
          okDisabled={foodOrderingState?.selectedProducts?.length === 0}
          onOk={postFoodOrderingCategoryProduct}
        >
          <Search
            searchCallback={(searchValue) => handleSearch(searchValue)}
            historyKey="product_group_products"
            placeholder={t("Search products...")}
          />

          <div className="product-select-table">
            {foodOrderingState.products ? (
              <div>
                {foodOrderingState.products?.map((product: IProduct, index: number) => {
                  const foundProduct = foodOrderingState.selectedProducts?.find(
                    (selectedProduct) => selectedProduct?.id === product?.id,
                  );

                  return (
                    <div
                      key={index}
                      className={classNames("table-row product-header", {})}
                      onClick={() => handleSelectProduct(index)}
                    >
                      <div>
                        <Checkbox size="medium" labelHidden checked={foundProduct ? true : false} onChange={() => {}} />
                      </div>
                      <div>
                        <p>{product?.preferred_title ? product?.preferred_title : product.title}</p>
                        {product?.subtitle && <p>{product.subtitle}</p>}
                      </div>
                      <div>{product.vendor_title}</div>
                      {/* <div>{singleVariant && product?.variants[0]?.sku}</div> */}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="spinner">
                <Spin />
              </div>
            )}
          </div>
        </Sheet>
      </div>

      <Popup
        open={foodOrderingState.deleteModalVisible}
        type="warning"
        title={t("Delete Category")}
        description={t("Are you sure you want to delete this category? This cannot be undone.")}
        onOk={handleDeleteCategory}
        okText={t("Delete")}
        onCancel={() => setFoodOrderingState((prevState) => ({ ...prevState, deleteModalVisible: false }))}
      />

      {/* Delete Product Group Product */}
      <Popup
        open={foodOrderingState.deleteProductVisibility}
        type="warning"
        title={t("Remove Products")}
        description={t("Are you sure you want to remove the food ordering category products")}
        onOk={() => deleteProducts()}
        okText={t("Delete")}
        onCancel={() => handleDeleteProductsVisibility()}
        backDropCancel={true}
      />
    </>
  );
}
